import { State } from 'redux/initialState';
import { useParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  ChangeEvent,
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { store } from 'redux/store';
import { SocietyEquityMarketStatus } from 'types/Society';

import actionTypes from 'constants/actionTypes';
import menuTypes from 'constants/menuTypes';
import { formatDateToInput } from 'constants/formats';

import { useTranslate } from 'hooks/useTranslate';
import { useGetActualSociety } from 'hooks/useGetActualSociety';

import { setModal } from 'modules/_shared/redux/modalActions';
import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';
import { getIncentiveOffer } from 'modules/incentivesMarket/redux/incentiveOfferActions';
import PublishIncentiveOfferModal from 'modules/incentivesMarket/modals/PublishIncentiveOffer';
import { useGetBeneficiaryOptions } from 'modules/incentivesMarket/hooks/useGetBeneficiaryOptions';
import { useGetBeneficiaryIncentivesMarketContext } from 'modules/incentivesMarket/hooks/useGetBeneficiaryIncentivesMarketContext';
import { CustomAsyncSelectOption } from 'modules/_shared/components/CustomAsyncSelect/types/CustomAsyncSelectOption';
import { IncentiveOffer, IncentiveOfferSellerParticipationLimits, IncentiveOfferType } from 'modules/incentivesMarket/types';
import { PublishIncentiveOfferModalProvider } from 'modules/incentivesMarket/modals/PublishIncentiveOffer/context/PublishIncentiveOfferModalContext';
import { mapBeneficiaryOptionToPartialBeneficiary } from 'modules/incentivesMarket/modals/PublishIncentiveOffer/utils/buildInitialPublisherBeneficiary';
import { sanitizeSellerParticipationLimitsForPayload } from 'modules/incentivesMarket/utils/getSellerLimitsSummary';
import { calculateSellTotalUnitsByTypeLimits } from 'modules/incentivesMarket/utils/calculateSellerSellLimits';

import errorSwal from 'utils/errorSwal';

import { addIncentiveOfferPageInitValue } from '../constants/addIncentiveOfferPageInitValue';
import { defaultSellerParticipationLimits } from '../constants/defaultSellerParticipationLimits';
import { AddIncentiveOfferPageContextType } from '../types/AddIncentiveOfferPageContextType';

import { useGetPrices } from '../hooks/useGetPrices';

export const AddIncentiveOfferPageContext =
  createContext<AddIncentiveOfferPageContextType>(addIncentiveOfferPageInitValue);

type AddIncentiveOfferPageProviderProps = {
  children: ReactNode;
  incentiveOfferType: IncentiveOfferType;
  actionType?: string;
};

export const AddIncentiveOfferPageProvider: FC<
  AddIncentiveOfferPageProviderProps
> = ({ children, incentiveOfferType, actionType = actionTypes.ADD }) => {
  const {
    societyId,
    beneficiaryId: beneficiaryIdParam,
    incentiveOfferId,
  } = useParams<{
    societyId: string;
    beneficiaryId: string;
    incentiveOfferId: string;
  }>();
  const beneficiaryIdQuery = new URLSearchParams(useLocation().search).get(
    'beneficiaryId'
  );
  const beneficiaryId = beneficiaryIdParam || beneficiaryIdQuery || '';

  const { t } = useTranslate();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );
  const isAdmin = useSelector(
    (state: State) => state.society?.role?.isAdmin ?? false
  );

  useGetActualSociety(societyId);
  useGetMenu(menuTypes.NULL);

  const [selectedPartner, setSelectedPartner] = useState<string>(
    beneficiaryId || ''
  );
  const [sharesToTransfer, setSharesToTransfer] = useState<number>(0);
  const [isSellAllMyShares, setIsSellAllMyShares] = useState<boolean>(false);
  const [isPartialAmountEnabled, setIsPartialAmountEnabled] =
    useState<boolean>(false);
  const [minUnitsPerAcceptance, setMinSharesPerAcceptance] =
    useState<number>(1);
  const [additionalNotes, setAdditionalNotes] = useState<string>('');
  const [price, setPrice] = useState<number>(0);
  const [isPricePerShare, setIsPricePerShare] = useState<boolean>(true);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [calculationDate, setCalculationDate] = useState<string>(() =>
    incentiveOfferType === IncentiveOfferType.BUY
      ? formatDateToInput(new Date())
      : ''
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadedOfferForEdit, setLoadedOfferForEdit] =
    useState<IncentiveOffer | null>(null);
  const [sellerParticipationLimits, setSellerParticipationLimits] =
    useState<IncentiveOfferSellerParticipationLimits>(defaultSellerParticipationLimits);

  const isSellOffer = incentiveOfferType === IncentiveOfferType.SELL;
  const isBuyOffer = incentiveOfferType === IncentiveOfferType.BUY;

  const { data: pricesData } = useGetPrices({
    price,
    sharesToTransfer,
    isPricePerShare,
  });

  const { data: beneficiaryOptionsData, actions: beneficiaryOptionsActions } =
    useGetBeneficiaryOptions({
      societyId,
      beneficiaryId,
    });

  const {
    data: beneficiaryMarketContext,
    isLoading: isLoadingBeneficiaryContext,
  } = useGetBeneficiaryIncentivesMarketContext({
    societyId,
    beneficiaryId: selectedPartner,
    excludeOfferId: incentiveOfferId,
    enabled: isSellOffer && Boolean(selectedPartner),
  });

  const maxSharesToTransfer = useMemo(() => {
    if (!isSellOffer) {
      return Infinity;
    }
    return beneficiaryMarketContext.maxSellUnits;
  }, [beneficiaryMarketContext.maxSellUnits, isSellOffer]);

  useEffect(() => {
    if (isSellAllMyShares && maxSharesToTransfer > 0) {
      setSharesToTransfer(maxSharesToTransfer);
    }
  }, [isSellAllMyShares, maxSharesToTransfer]);

  // SELL custom limits: derive the offer total from the per-type caps (types left
  // at 0 are excluded). Keeps `sharesToTransfer` editable as an overall cap below.
  useEffect(() => {
    if (!isSellOffer || sellerParticipationLimits.allowSellAll) {
      return;
    }
    const derivedUnits = calculateSellTotalUnitsByTypeLimits({
      plans: beneficiaryMarketContext.plans,
      sellerParticipationLimits,
    });
    setSharesToTransfer(derivedUnits);
    setIsSellAllMyShares(false);
  }, [sellerParticipationLimits, beneficiaryMarketContext.plans, isSellOffer]);

  useEffect(() => {
    if (
      actionType !== actionTypes.EDIT ||
      !incentiveOfferId ||
      !actualSociety?._id
    ) {
      setLoadedOfferForEdit(null);
      return undefined;
    }

    let cancelled = false;

    const loadOffer = async () => {
      setIsLoading(true);
      try {
        const offer = await store.dispatch(getIncentiveOffer(incentiveOfferId));
        if (cancelled) {
          return;
        }

        if (
          !offer ||
          String(offer.society) !== String(actualSociety._id) ||
          offer.type !== incentiveOfferType
        ) {
          setLoadedOfferForEdit(null);
          errorSwal(t('OfferNotFound'));
          return;
        }

        setLoadedOfferForEdit(offer);

        const pid =
          typeof offer.beneficiary === 'string'
            ? offer.beneficiary
            : offer.beneficiary?._id || '';
        setSelectedPartner(pid || '');
        setSharesToTransfer(offer.unitsCount?.total ?? 0);
        setIsPartialAmountEnabled(Boolean(offer.isPartialAmountEnabled));
        setMinSharesPerAcceptance(offer.minUnitsPerAcceptance ?? 1);
        setPrice(offer.unitPrice ?? 0);
        setIsPricePerShare(true);
        setStartDate(formatDateToInput(offer.startDate));
        setEndDate(formatDateToInput(offer.endDate));
        if (offer.type === IncentiveOfferType.BUY) {
          setCalculationDate(
            formatDateToInput(offer.calculationDate || offer.startDate)
          );
        }
        if (offer.sellerParticipationLimits) {
          setSellerParticipationLimits({
            ...defaultSellerParticipationLimits,
            ...offer.sellerParticipationLimits,
          });
        }
      } catch {
        if (!cancelled) {
          setLoadedOfferForEdit(null);
          errorSwal(t('OfferNotFound'));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadOffer();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `t` is recreated every render and causes an infinite fetch loop
  }, [actionType, incentiveOfferId, actualSociety?._id, incentiveOfferType]);

  const handleChangeSharesToTransfer = (value: number) => {
    setSharesToTransfer(value);
    if (value !== maxSharesToTransfer) {
      setIsSellAllMyShares(false);
    }
    if (isPartialAmountEnabled && value > 0) {
      setMinSharesPerAcceptance((prev) => Math.min(prev, value));
    }
  };

  const handleChangeIsSellAllMyShares = () => {
    const newValue = !isSellAllMyShares;
    setIsSellAllMyShares(newValue);
    setSharesToTransfer(newValue ? maxSharesToTransfer : 0);
  };

  const handleChangeIsPartialAmountEnabled = () => {
    setIsPartialAmountEnabled((prev) => !prev);
  };

  const handleChangeMinSharesPerAcceptance = (value: number) => {
    setMinSharesPerAcceptance(value);
  };

  const handleChangeAdditionalNotes = (value: string) => {
    setAdditionalNotes(value);
  };

  const handleChangePrice = (value: number) => {
    setPrice(value);
  };

  const handleChangeIsPricePerShare = () => {
    setIsPricePerShare((prev) => !prev);
  };

  const handleChangeStartDate = (event: ChangeEvent<HTMLInputElement>) => {
    setStartDate(event.target.value);
  };

  const handleChangeEndDate = (event: ChangeEvent<HTMLInputElement>) => {
    setEndDate(event.target.value);
  };

  const handleChangeCalculationDate = (event: ChangeEvent<HTMLInputElement>) => {
    setCalculationDate(event.target.value);
  };

  const handleSetDatePreset = (days: number) => {
    const today = new Date();
    const end = new Date();
    end.setDate(today.getDate() + days);

    setStartDate(formatDateToInput(today));
    setEndDate(formatDateToInput(end));
  };

  const handleChangeAllowSellAll = (allowSellAll: boolean) => {
    setSellerParticipationLimits((prev) => ({
      ...prev,
      allowSellAll,
    }));
  };

  const handleChangeSellerLimit = (
    field: keyof IncentiveOfferSellerParticipationLimits,
    value: number
  ) => {
    setSellerParticipationLimits((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleChangePartner = (
    selectedOption: CustomAsyncSelectOption | null
  ) => {
    setSelectedPartner(selectedOption ? selectedOption.value : '');
    beneficiaryOptionsActions.handleChangeBeneficiaryOption(selectedOption);
    setSharesToTransfer(0);
    setIsSellAllMyShares(false);
  };

  const handleReviewIncentiveOffer = () => {
    if (!societyId || !selectedPartner) return;
    if (actionType === actionTypes.EDIT && !loadedOfferForEdit) return;

    const incentiveOffer: Partial<IncentiveOffer> & {
      className: string;
      minUnitsPerAcceptance?: number;
      additionalNotes?: string;
    } = {
      ...(actionType === actionTypes.EDIT && loadedOfferForEdit
        ? {
            _id: loadedOfferForEdit._id,
            documents: loadedOfferForEdit.documents || [],
            createdAt: loadedOfferForEdit.createdAt,
            updatedAt: loadedOfferForEdit.updatedAt,
          }
        : {}),
      society: societyId,
      beneficiary: selectedPartner,
      unitClass: null,
      className: '-',
      unitsCount: {
        total: sharesToTransfer,
        agreed: 0,
        approved: 0,
      },
      unitPrice: pricesData.pricePerShare,
      startDate,
      endDate,
      type: incentiveOfferType,
      isPartialAmountEnabled,
      minUnitsPerAcceptance: isPartialAmountEnabled
        ? minUnitsPerAcceptance
        : undefined,
      additionalNotes: additionalNotes || undefined,
      documents: [],
      sellerParticipationLimits: sanitizeSellerParticipationLimitsForPayload(
        sellerParticipationLimits
      ),
      ...(isBuyOffer ? { calculationDate } : {}),
    };

    store.dispatch(
      setModal(
        <PublishIncentiveOfferModalProvider
          incentiveOffer={incentiveOffer}
          modalActionType={actionType}
          size="lg"
          publisherPartner={mapBeneficiaryOptionToPartialBeneficiary(
            selectedPartner,
            beneficiaryOptionsData.selectedBeneficiaryOption
          )}
        >
          <PublishIncentiveOfferModal />
        </PublishIncentiveOfferModalProvider>
      )
    );
  };

  return (
    <AddIncentiveOfferPageContext.Provider
      value={{
        data: {
          incentiveOfferType,
          actionType,
          beneficiaryOptions: beneficiaryOptionsData.beneficiaryOptions || [],
          selectedBeneficiaryOption: beneficiaryOptionsData.selectedBeneficiaryOption,
          beneficiaryCurrentUnitsCount:
            beneficiaryMarketContext.totalAvailableUnits,
          beneficiaryCif: beneficiaryMarketContext.cif,
          beneficiaryCifGroupIds: beneficiaryMarketContext.beneficiaryIds,
          plansBreakdown: beneficiaryMarketContext.plans,
          reservedUnits: beneficiaryMarketContext.reservedUnits,
          sharesToTransfer,
          maxSharesToTransfer,
          isSellAllMyShares,
          isPartialAmountEnabled,
          minUnitsPerAcceptance,
          additionalNotes,
          price,
          pricePerShare: pricesData.pricePerShare,
          totalOfferPrice: pricesData.totalOfferPrice,
          societyId: societyId || '',
          societyName: actualSociety?.name || '',
          isPricePerShare,
          startDate,
          endDate,
          calculationDate,
          isBeneficiaryView: !!beneficiaryId,
          isAdmin,
          isReadOnly: false,
          isLoading: isLoading || !actualSociety,
          isLoadingBeneficiaryContext,
          isMarketClosed:
            actualSociety?.configuration?.incentivesMarket?.status ===
              SocietyEquityMarketStatus.CLOSED ||
            actualSociety?.configuration?.incentivesMarket?.status ===
              SocietyEquityMarketStatus.UNAVAILABLE,
          sellerParticipationLimits,
        },
        actions: {
          handleChangePartner,
          handleSearchBeneficiaryByTerm:
            beneficiaryOptionsActions.handleSearchBeneficiaryByTerm,
          handleChangeSharesToTransfer,
          handleChangeIsSellAllMyShares,
          handleChangeIsPartialAmountEnabled,
          handleChangeMinSharesPerAcceptance,
          handleChangeAdditionalNotes,
          handleChangePrice,
          handleChangeIsPricePerShare,
          handleChangeStartDate,
          handleChangeEndDate,
          handleChangeCalculationDate,
          handleSetDatePreset,
          handleChangeAllowSellAll,
          handleChangeSellerLimit,
          handleReviewIncentiveOffer,
        },
      }}
    >
      {children}
    </AddIncentiveOfferPageContext.Provider>
  );
};

export const useAddIncentiveOfferPage = () => {
  const context = useContext(AddIncentiveOfferPageContext);
  if (!context) {
    throw new Error(
      'useAddIncentiveOfferPage must be used within a AddIncentiveOfferPageProvider'
    );
  }
  return context;
};
